import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  type ServiceAreaApi,
  adminFetchServiceAreas,
  adminSaveServiceArea,
  adminDeleteServiceArea,
} from '@/api/client';
import { serviceAreas as mockServiceAreas } from '@/mocks/serviceAreas';

function mockToAreaApi(m: typeof mockServiceAreas[number]): ServiceAreaApi {
  return {
    id: m.id,
    city_name: m.name,
    state: m.state,
    display_order: m.order,
    active: m.active ? 1 : 0,
  };
}

function normalizeActive(val: number | boolean): boolean {
  return val === 1 || val === true;
}

export default function AdminAreasManager() {
  const { t } = useTranslation('home');
  const [areas, setAreas] = useState<ServiceAreaApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [usingMock, setUsingMock] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<ServiceAreaApi | null>(null);
  const [saving, setSaving] = useState(false);

  const emptyForm: ServiceAreaApi = {
    id: 0,
    city_name: '',
    state: 'CA',
    display_order: areas.length + 1,
    active: 1,
  };

  const [form, setForm] = useState<ServiceAreaApi>({ ...emptyForm });

  const loadAreas = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setUsingMock(false);
    try {
      const { data, error } = await adminFetchServiceAreas();
      if (error) {
        // Backend unreachable — show defaults as editable reference
        setAreas(mockServiceAreas.map(mockToAreaApi));
        setFetchError(error);
        setUsingMock(true);
      } else if (data.length === 0) {
        // Backend reachable but no areas yet — preload defaults so admin can edit/save
        setAreas(mockServiceAreas.map(mockToAreaApi));
        setUsingMock(true);
      } else {
        setAreas(data);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setAreas(mockServiceAreas.map(mockToAreaApi));
      setFetchError(msg);
      setUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  const openAdd = () => {
    setEditingArea(null);
    setForm({ ...emptyForm, display_order: areas.length + 1 });
    setModalOpen(true);
  };

  const openEdit = (area: ServiceAreaApi) => {
    setEditingArea(area);
    setForm({ ...area });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingArea(null);
    setSaving(false);
  };

  const handleSave = async () => {
    if (!form.city_name.trim()) {
      Swal.fire({
        title: t('admin_missing_info'),
        text: t('admin_enter_city'),
        icon: 'warning',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
      return;
    }
    if (!form.state.trim()) {
      Swal.fire({
        title: t('admin_missing_info'),
        text: t('admin_enter_state'),
        icon: 'warning',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
      return;
    }

    setSaving(true);
    const payload = {
      ...form,
      display_order: Number(form.display_order) || 1,
      active: form.active === true || form.active === 1 ? 1 : 0,
    };
    const res = await adminSaveServiceArea(payload);
    setSaving(false);
    if (res.success && res.data) {
      Swal.fire({
        title: editingArea ? t('admin_area_updated') : t('admin_area_added'),
        text: t('admin_api_saved'),
        icon: 'success',
        confirmButtonColor: '#3DBFA0',
        timer: 2000,
        timerProgressBar: true,
      });
      await loadAreas();
      closeModal();
    } else {
      Swal.fire({
        title: t('admin_api_error'),
        text: res.message || '',
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    }
  };

  const handleDelete = (area: ServiceAreaApi) => {
    Swal.fire({
      title: `${t('admin_delete')}?`,
      text: `${t('admin_remove_area')} "${area.city_name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#1A2E4A',
      confirmButtonText: t('admin_delete'),
      cancelButtonText: t('admin_cancel'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const res = await adminDeleteServiceArea(area.id);
        setLoading(false);
        if (res.success) {
          Swal.fire({
            title: t('admin_api_deleted'),
            icon: 'success',
            confirmButtonColor: '#3DBFA0',
            timer: 1500,
            timerProgressBar: true,
          });
          await loadAreas();
        } else {
          Swal.fire({
            title: t('admin_api_error'),
            text: res.message || '',
            icon: 'error',
            confirmButtonColor: '#3DBFA0',
            confirmButtonText: t('admin_ok'),
          });
        }
      }
    });
  };

  const handleToggleActive = async (area: ServiceAreaApi) => {
    const payload = {
      ...area,
      active: normalizeActive(area.active) ? 0 : 1,
    };
    const res = await adminSaveServiceArea(payload);
    if (res.success) {
      await loadAreas();
    }
  };

  const activeCount = areas.filter((a) => normalizeActive(a.active)).length;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-brand-dark font-heading">
            {t('admin_areas_manager')}
          </h2>
          <p className="text-sm text-brand-muted mt-1">{t('admin_manage_areas')}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-brand-muted">
            {activeCount} {t('admin_of_active')} {areas.length}
          </span>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-sm"
          >
            <i className="ri-add-line" />
            {t('admin_add_area')}
          </button>
        </div>
      </div>

      {/* Default areas banner */}
      {usingMock && !loading && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
            <i className="ri-information-line text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              {fetchError ? 'Backend not reachable' : 'Default areas loaded'}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              {fetchError
                ? `Showing default areas from the landing page. (${fetchError})`
                : 'These areas are displayed on the public site but are not saved in the backend yet. Edit and save them here to enable full management.'}
            </p>
          </div>
          <button
            onClick={loadAreas}
            className="shrink-0 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />Retry
          </button>
        </div>
      )}

      {loading && areas.length === 0 ? (
        <div className="py-12 text-center text-brand-muted text-sm">
          <i className="ri-loader-4-line animate-spin text-xl mr-2" />
          {t('admin_loading_data')}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: t('admin_total_areas'),
                value: areas.length,
                icon: 'ri-map-pin-line',
                color: 'text-brand-mint',
              },
              {
                label: t('admin_active_short'),
                value: activeCount,
                icon: 'ri-check-line',
                color: 'text-green-500',
              },
              {
                label: t('admin_inactive_short'),
                value: areas.length - activeCount,
                icon: 'ri-close-line',
                color: 'text-gray-400',
              },
              {
                label: t('admin_states'),
                value: [...new Set(areas.map((a) => a.state))].length,
                icon: 'ri-map-2-line',
                color: 'text-blue-500',
              },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3"
              >
                <div
                  className={`w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center ${stat.color}`}
                >
                  <i className={`${stat.icon} text-lg`} />
                </div>
                <div>
                  <p className="text-xl font-bold text-brand-dark">{stat.value}</p>
                  <p className="text-xs text-brand-muted">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">
                      {t('admin_city_name')}
                    </th>
                    <th className="text-left px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">
                      {t('admin_state')}
                    </th>
                    <th className="text-center px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">
                      {t('admin_slide_order')}
                    </th>
                    <th className="text-center px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">
                      {t('admin_status')}
                    </th>
                    <th className="text-right px-5 py-3.5 font-semibold text-brand-dark text-xs uppercase tracking-wider">
                      {t('admin_actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {areas
                    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
                    .map((area) => (
                      <tr
                        key={area.id}
                        className={`hover:bg-gray-50/50 transition-colors ${!normalizeActive(area.active) ? 'opacity-60' : ''}`}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-brand-light/60 flex items-center justify-center shrink-0">
                              <i className="ri-map-pin-line text-brand-mint text-sm" />
                            </div>
                            <span className="text-sm font-medium text-brand-dark">
                              {area.city_name}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-brand-muted">{area.state}</td>
                        <td className="px-5 py-4 text-center">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-xs font-semibold text-brand-dark">
                            {area.display_order}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <button
                            onClick={() => handleToggleActive(area)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors cursor-pointer ${normalizeActive(area.active) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                          >
                            <i className={normalizeActive(area.active) ? 'ri-eye-line' : 'ri-eye-off-line'} />
                            {normalizeActive(area.active) ? t('admin_active') : t('admin_inactive')}
                          </button>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEdit(area)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                              aria-label={t('admin_edit')}
                            >
                              <i className="ri-edit-line text-brand-dark" />
                            </button>
                            <button
                              onClick={() => handleDelete(area)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                              aria-label={t('admin_delete')}
                            >
                              <i className="ri-delete-bin-line text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {areas.length === 0 && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-2-line text-gray-300 text-3xl" />
                </div>
                <p className="text-sm font-medium text-brand-dark">
                  {t('admin_no_service_areas')}
                </p>
                <p className="text-xs text-brand-muted mt-1">{t('admin_add_first_area')}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-dark/40 backdrop-blur-sm p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl my-8 animate-[fadeIn_0.2s_ease-out]">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-brand-dark font-heading">
                  {editingArea ? t('admin_edit') : t('admin_add')} {t('admin_service_areas_short')}
                </h3>
                <p className="text-xs text-brand-muted mt-0.5">
                  {editingArea ? t('admin_manage_areas') : t('admin_add_area')}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-brand-dark" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <i
                    className={`${normalizeActive(form.active) ? 'ri-eye-line text-brand-mint' : 'ri-eye-off-line text-gray-400'} text-lg`}
                  />
                  <div>
                    <p className="text-sm font-semibold text-brand-dark">
                      {normalizeActive(form.active) ? t('admin_active') : t('admin_inactive')}
                    </p>
                    <p className="text-xs text-brand-muted">
                      {normalizeActive(form.active) ? t('admin_visible') : t('admin_hidden')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    setForm({ ...form, active: normalizeActive(form.active) ? 0 : 1 })
                  }
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer ${normalizeActive(form.active) ? 'bg-brand-mint' : 'bg-gray-300'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${normalizeActive(form.active) ? 'translate-x-6' : ''}`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-dark mb-1.5">
                  {t('admin_city_name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.city_name}
                  onChange={(e) => setForm({ ...form, city_name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                  placeholder={t('admin_placeholder_city_name')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-dark mb-1.5">
                    {t('admin_state')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    placeholder={t('admin_placeholder_state')}
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-dark mb-1.5">
                    {t('admin_display_order')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={form.display_order}
                    onChange={(e) =>
                      setForm({ ...form, display_order: parseInt(e.target.value) || 1 })
                    }
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
              <p className="text-xs text-brand-muted">{t('admin_save_to_backend')}</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-brand-dark hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  {t('admin_cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      {t('admin_saving')}
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line" />
                      {editingArea ? t('admin_save_changes') : t('admin_add_area')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}